/**
 * Vendor Check-In Types
 *
 * Type definitions for the vendor check-in lookup feature.
 * Supports QR scanning, code entry, phone lookup, and name search.
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

/** All supported check-in method types */
export const CheckInMethodTypeEnum = z.enum([
  'QR_SCAN',
  'MANUAL',
  'CODE_ENTRY',
  'PHONE_LOOKUP',
  'NAME_SEARCH',
]);
export type CheckInMethodType = z.infer<typeof CheckInMethodTypeEnum>;

// ============================================
// CONFIRMATION CODE
// ============================================

/**
 * Confirmation code format: LOV{YY}-XXXXXX
 * YY = 2-digit year, X = base32-crockford character (no ambiguous chars)
 * Charset: 23456789ABCDEFGHJKMNPQRSTUVWXYZ
 */
export const CONFIRMATION_CODE_REGEX = /^LOV\d{2}-[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{6}$/;

export const ConfirmationCodeSchema = z
  .string()
  .regex(CONFIRMATION_CODE_REGEX, 'Invalid confirmation code format. Expected LOV{YY}-XXXXXX');
export type ConfirmationCode = z.infer<typeof ConfirmationCodeSchema>;

// ============================================
// LOOKUP QUERY SCHEMAS
// ============================================

export const CodeLookupParamSchema = z.object({
  code: ConfirmationCodeSchema,
});
export type CodeLookupParam = z.infer<typeof CodeLookupParamSchema>;

export const PhoneLookupQuerySchema = z.object({
  phone: z.string().min(4, 'Enter at least 4 digits').max(15),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  venueId: z.string().uuid().optional(),
});
export type PhoneLookupQuery = z.infer<typeof PhoneLookupQuerySchema>;

export const NameSearchQuerySchema = z.object({
  query: z.string().min(2, 'Enter at least 2 characters').max(100),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  venueId: z.string().uuid().optional(),
});
export type NameSearchQuery = z.infer<typeof NameSearchQuerySchema>;

export const TodayArrivalsQuerySchema = z.object({
  venueId: z.string().uuid().optional(),
  status: z.enum(['ALL', 'PENDING', 'CONFIRMED', 'CHECKED_IN']).default('ALL'),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(50),
});
export type TodayArrivalsQuery = z.infer<typeof TodayArrivalsQuerySchema>;

// ============================================
// LOOKUP RESULT SCHEMAS
// ============================================

/** Booking preview shown in lookup results (minimal fields for quick display) */
export const CheckInLookupResultSchema = z.object({
  bookingId: z.string(),
  confirmationCode: z.string().nullable(),
  customerName: z.string(),
  customerPhone: z.string(), // masked: ***-***-1234
  bookingDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  venueName: z.string(),
  venueId: z.string(),
  status: z.enum(['PENDING', 'CONFIRMED', 'CHECKED_IN', 'CANCELLED', 'COMPLETED', 'NO_SHOW']),
  totalAmount: z.number(),
  paymentStatus: z.enum(['PAID', 'PENDING', 'PARTIAL', 'DEPOSIT_PAID']).optional(),
  checkInTime: z.string().nullable(),
  isEarlyCheckIn: z.boolean().optional(),
  earlyByMinutes: z.number().optional(),
});
export type CheckInLookupResult = z.infer<typeof CheckInLookupResultSchema>;

export const CheckInLookupResultsSchema = z.array(CheckInLookupResultSchema);
export type CheckInLookupResults = z.infer<typeof CheckInLookupResultsSchema>;

// ============================================
// ENHANCED CHECK-IN REQUEST/RESPONSE
// ============================================

export const EnhancedCheckInSchema = z.object({
  method: CheckInMethodTypeEnum,
  confirmationCode: z.string().optional(),
  earlyCheckInAcknowledged: z.boolean().optional(),
});
export type EnhancedCheckInRequest = z.infer<typeof EnhancedCheckInSchema>;

export const EnhancedCheckInResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  checkInTime: z.string(),
  bookingId: z.string(),
  isEarlyCheckIn: z.boolean().optional(),
  earlyByMinutes: z.number().optional(),
});
export type EnhancedCheckInResponse = z.infer<typeof EnhancedCheckInResponseSchema>;

// ============================================
// TODAY'S ARRIVALS
// ============================================

export const TodayArrivalItemSchema = z.object({
  bookingId: z.string(),
  confirmationCode: z.string().nullable(),
  customerName: z.string(),
  customerPhone: z.string(), // masked
  startTime: z.string(),
  endTime: z.string(),
  venueName: z.string(),
  venueId: z.string(),
  status: z.enum(['PENDING', 'CONFIRMED', 'CHECKED_IN', 'CANCELLED', 'COMPLETED', 'NO_SHOW']),
  paymentStatus: z.enum(['PAID', 'PENDING', 'PARTIAL', 'DEPOSIT_PAID']).optional(),
  totalAmount: z.number(),
  checkInTime: z.string().nullable(),
  isOverdue: z.boolean().optional(),
  isNoShowEligible: z.boolean().optional(),
});
export type TodayArrivalItem = z.infer<typeof TodayArrivalItemSchema>;

export const TodayArrivalsResponseSchema = z.object({
  date: z.string(),
  total: z.number(),
  checkedIn: z.number(),
  pending: z.number(),
  items: z.array(TodayArrivalItemSchema),
});
export type TodayArrivalsResponse = z.infer<typeof TodayArrivalsResponseSchema>;
